import { useEffect, useState } from "react";
import { Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getWebsiteIntake, saveWebsiteIntake } from "@/lib/api";

// Settings → liftr.fit intake: what the website shows for this gym. The date and close time
// default to the next Monday (at least six days away) and the Thursday before, 9 PM.
// "Joined this week" is counted from 6 Week Challenge sales. No seat caps: batches are open.
const SITE_GYMS = ["hsr", "jp", "wf", "hennur", "banjara", "colombo7", "nawala", "dubai"];
const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</div>
    {children}
    {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
  </label>
);
const input = "w-full rounded-md bg-[#0a0a0a] border border-[#262626] h-10 px-2 text-sm text-zinc-100";

export default function WebsiteIntakeCard({ canEdit }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ intake_date: "", closes_at: "", website_gym: "", lead_webhook: "" });
  const [saving, setSaving] = useState(false);

  const load = () => getWebsiteIntake().then((d) => {
    setData(d);
    setForm({
      intake_date: d.settings?.intake_date || "",
      closes_at: (d.settings?.closes_at || "").slice(0, 16),
      website_gym: d.website_gym || "",
      lead_webhook: d.lead_webhook || "",
    });
  }).catch(() => setData({ live: null }));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const live = data.live;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveWebsiteIntake({
        intake_date: form.intake_date || null,
        closes_at: form.closes_at || null,
        website_gym: form.website_gym || null,
        lead_webhook: form.lead_webhook.trim() || null,
      });
      setData(saved);
      toast.success("liftr.fit updated for this gym");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <section className="card-surface p-6 space-y-4" data-testid="website-intake">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-zinc-400" /><h3 className="font-semibold">liftr.fit intake</h3></div>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            What the website shows for this gym. Leave the date blank and it&apos;s the next Monday (at least six days away);
            applications close the Thursday before at 9 PM. &quot;Joined this week&quot; is counted from 6 Week Challenge sales.
          </p>
        </div>
        {live && (
          <div className="text-right text-xs text-zinc-400">
            <div>Showing now: <span className="text-zinc-100">intake {live.intake}</span></div>
            <div>{live.joined_week} joined this week</div>
          </div>
        )}
      </div>
      {!canEdit ? (
        <p className="text-sm text-zinc-500">Managers can change the intake date.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Intake date" hint="A Monday. Blank = next default Monday.">
              <input type="date" className={input} value={form.intake_date} onChange={(e) => set("intake_date", e.target.value)} data-testid="website-intake-date" />
            </Field>
            <Field label="Applications close" hint="Blank = Thursday before, 9 PM.">
              <input type="datetime-local" className={input} value={form.closes_at} onChange={(e) => set("closes_at", e.target.value)} data-testid="website-closes-at" />
            </Field>
            <Field label="Website gym" hint="Which liftr.fit gym this is.">
              <select className={input} value={form.website_gym} onChange={(e) => set("website_gym", e.target.value)} data-testid="website-gym">
                <option value="">— match by name —</option>
                {SITE_GYMS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
          </div>
          <Field label="GoHighLevel inbound webhook for website leads" hint="Optional. From a workflow in this gym's GoHighLevel with an Inbound Webhook trigger: every application is posted there too (first_name, last_name, phone, email, gym, goal, batch, call_when, source=liftr.fit), so your workflow runs on it like it did on the old form. The contact, opportunity and call task are created either way.">
            <input type="url" className={input} value={form.lead_webhook} onChange={(e) => set("lead_webhook", e.target.value)} placeholder="https://services.leadconnectorhq.com/hooks/…/webhook-trigger/…" data-testid="website-lead-webhook" />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="website-intake-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
